import React, { useState, useEffect } from "react";
import { APIProvider, Map, Marker, InfoWindow, useMap } from "@vis.gl/react-google-maps";
import { useNavigate } from "react-router-dom";
import { fmtAmount } from "@/lib/stayflowApi";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Fits the viewport around all plotted points (+ the user location if present).
const FitBounds = ({ geo, userLoc }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google) return;
    if (geo.length <= 1 && !userLoc) return;
    const b = new window.google.maps.LatLngBounds();
    geo.forEach((p) => b.extend({ lat: p.lat, lng: p.lng }));
    if (userLoc?.lat != null) b.extend({ lat: userLoc.lat, lng: userLoc.lng });
    map.fitBounds(b, 64);
  }, [map, geo, userLoc]);
  return null;
};

/**
 * Google Maps view plotting property markers from the app's resolved
 * location data. Replaces the previous Leaflet/OpenStreetMap implementation.
 */
export const StaysMap = ({ points = [], userLoc = null, height = 340, testid = "stays-map" }) => {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const geo = points.filter((p) => p.lat != null && p.lng != null);

  if (!KEY) {
    return (
      <div className="rounded-3xl border border-black/10 bg-paper/70 flex items-center justify-center text-ink-soft text-sm" style={{ height }} data-testid={`${testid}-nokey`}>
        Map isn't configured yet.
      </div>
    );
  }
  if (geo.length === 0) {
    return (
      <div className="rounded-3xl border border-black/10 bg-paper/70 flex items-center justify-center text-ink-soft text-sm" style={{ height }} data-testid="stays-map-empty">
        Map locations are being prepared…
      </div>
    );
  }

  const center = userLoc?.lat != null ? { lat: userLoc.lat, lng: userLoc.lng } : { lat: geo[0].lat, lng: geo[0].lng };
  const activeP = active ? geo.find((x) => x.id === active) : null;

  return (
    <div className="relative rounded-3xl overflow-hidden border border-black/10 shadow-sm" data-testid={testid}>
      <APIProvider apiKey={KEY}>
        <Map
          style={{ width: "100%", height }}
          defaultCenter={center}
          defaultZoom={geo.length > 1 ? 11 : 13}
          gestureHandling="greedy"
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={true}
          clickableIcons={false}
        >
          <FitBounds geo={geo} userLoc={userLoc} />
          {geo.map((p) => (
            <Marker key={p.id} position={{ lat: p.lat, lng: p.lng }} title={p.name || "Stay"} onClick={() => setActive(p.id)} />
          ))}
          {userLoc?.lat != null && (
            <Marker
              position={{ lat: userLoc.lat, lng: userLoc.lng }}
              title="You are here"
              icon={window.google ? { path: window.google.maps.SymbolPath.CIRCLE, scale: 7, fillColor: "#2563eb", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 } : undefined}
            />
          )}
          {activeP && (
            <InfoWindow position={{ lat: activeP.lat, lng: activeP.lng }} onCloseClick={() => setActive(null)}>
              <div style={{ minWidth: 168, fontFamily: "Manrope, sans-serif" }}>
                <strong style={{ fontFamily: "Outfit, sans-serif", fontSize: 14, color: "#141414" }}>{activeP.name || "Stay"}</strong>
                <div style={{ color: "#5C5C5C", fontSize: 12, marginTop: 2 }}>{[activeP.city, activeP.state].filter(Boolean).join(", ")}</div>
                <div style={{ color: "#141414", fontSize: 13, fontWeight: 700, marginTop: 6 }}>{fmtAmount(activeP.min_price_incl_gst ?? activeP.min_price_per_night)} <span style={{ fontWeight: 400, color: "#5C5C5C" }}>/ night incl. GST</span></div>
                <button
                  onClick={() => navigate(`/stays/${activeP.id}`)}
                  style={{ marginTop: 8, color: "#FF1A1A", fontSize: 12, fontWeight: 600, background: "none", border: "none", padding: 0, cursor: "pointer" }}
                  data-testid={`map-view-${activeP.id}`}
                >
                  View &amp; book →
                </button>
              </div>
            </InfoWindow>
          )}
        </Map>
      </APIProvider>
    </div>
  );
};

export default StaysMap;
